import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
	useCreateStatementPdfJobMutation,
	useDownloadStatementPdfFileMutation,
	useGetStatementPdfConfigQuery,
	useLazyGetStatementPdfJobStatusQuery,
	usePreviewStatementPdfMutation,
} from '../../../../slices/dashboardApiSlice';
import { useAddOrderContractsMutation, useAddOrderInvoicesMutation } from '../../../../slices/ordersApiSlice';
import { useAppContext } from '../../../../providers/AppProvider';
import { CHART_BUCKETS_RECENT_START_INDEX, CHART_LABELS } from '../overdueChartConstants';
import { STATEMENT_PDF_FONT_FACE_CSS, STATEMENT_PDF_PREVIEW_SCOPED_CSS, STATEMENT_PDF_TEMPLATE, STATEMENT_PDF_TABLE_COLUMN_COUNT } from 'pringxsoft-shared';
import { mergeStatementPdfTemplate } from 'pringxsoft-shared/buildStatementPdfHtml.js';

/** 对账单预览页（JSX）：版式 CSS 与 `shared/buildStatementPdfHtml.js` 同源；表格外壳的内联布局应与该文件中的 `.page` 结构保持一致，改动时请双边对齐。 */

function paymentStageWithTermDays(row) {
	const title = row.title;
	const n = Number(row.paymentTermDays);
	if (!Number.isFinite(n)) return title;
	return `${title} · 账期${Math.max(0, Math.floor(n))}天`;
}

function dateTextFromBackendField(value) {
	if (!value) return '';
	return String(value).slice(0, 10).replace(/-/g, '/');
}

function isContractNumberMissing(row) {
	const v = String(row?.contractNumber ?? '').trim();
	return !v || v === '-' || v === '—';
}

function isInvoiceNumberMissing(row) {
	const v = String(row?.invoiceNumber ?? '').trim();
	return !v || v === '-' || v === '—';
}

function splitDisplayNumbers(raw) {
	const v = String(raw ?? '').trim();
	if (!v || v === '-' || v === '—') return ['—'];
	const parts = v
		.split(/[\n,，、]|(?:\s+\/\s+)/)
		.map((s) => s.trim())
		.filter(Boolean);
	return parts.length > 0 ? parts : ['—'];
}

/** 收集从节点到 document 的可滚动祖先（含 overflow:auto/scroll），用于在滚动时关闭 Popover（避免浮层随滚动漂移） */
function getScrollableAncestors(node) {
	const list = [];
	let el = node;
	while (el && el !== document.documentElement) {
		const st = window.getComputedStyle(el);
		const oy = st.overflowY;
		const ox = st.overflowX;
		if (oy === 'auto' || oy === 'scroll' || oy === 'overlay' || ox === 'auto' || ox === 'scroll' || ox === 'overlay') {
			list.push(el);
		}
		el = el.parentElement;
	}
	return list;
}

/** 与 Overdue 柱状图 X 轴桶一致：索引 0 最严重（深红）→ 近期/远期渐缓 */
const useChartBucketSeverityColors = () => {
	const { getThemeColor } = useAppContext();
	return useMemo(
		() => ['#C41E3A', getThemeColor('danger'), '#FF6B35', getThemeColor('warning'), getThemeColor('primary'), '#4A90E2', '#5BC0DE', '#6BB6FF', '#7ED321', getThemeColor('success')],
		[getThemeColor],
	);
};

const StatementPdfPreviewModal = ({ show, onHide, seedOrder, snapshotChartIndex }) => {
	const { data: statementPdfConfig, isLoading: statementPdfConfigLoading } = useGetStatementPdfConfigQuery(undefined, {
		skip: !show,
		refetchOnMountOrArgChange: true,
	});
	const templateFromServer = useMemo(() => statementPdfConfig?.template || {}, [statementPdfConfig]);
	const tpl = useMemo(() => mergeStatementPdfTemplate(templateFromServer), [templateFromServer]);
	const tenantLogoUrl = statementPdfConfig?.tenantLogoUrl || '';
	const tenantDisplayName = statementPdfConfig?.tenantDisplayName || '';
	const isDark = useSelector((state) => state.config.isDark);
	const chartBucketSeverityColors = useChartBucketSeverityColors();
	const [step, setStep] = useState('range');
	const [selectedBuckets, setSelectedBuckets] = useState(() => new Set([0, 1, 2]));
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [previewScale, setPreviewScale] = useState(1);
	const [toolbarExpanded, setToolbarExpanded] = useState(false);
	const [toolbarHovering, setToolbarHovering] = useState(false);
	const [previewStatementPdf] = usePreviewStatementPdfMutation();
	const [createStatementPdfJob] = useCreateStatementPdfJobMutation();
	const [downloadStatementPdfFile] = useDownloadStatementPdfFileMutation();
	const [triggerGetJobStatus] = useLazyGetStatementPdfJobStatusQuery();
	const [previewPayload, setPreviewPayload] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState('');
	const previewReqRef = useRef(0);
	const [addOrderContracts] = useAddOrderContractsMutation();
	const [addOrderInvoices] = useAddOrderInvoicesMutation();
	const [contractDraftByOrderId, setContractDraftByOrderId] = useState({});
	const [contractSaveErrorByOrderId, setContractSaveErrorByOrderId] = useState({});
	const [invoiceDraftByOrderId, setInvoiceDraftByOrderId] = useState({});
	const [invoiceSaveErrorByOrderId, setInvoiceSaveErrorByOrderId] = useState({});
	const [savingOrderId, setSavingOrderId] = useState(null);
	const [editingContractOrderId, setEditingContractOrderId] = useState(null);
	const [statementContractPopoverPos, setStatementContractPopoverPos] = useState(null); // { top, left, arrowLeft }
	const [editingInvoiceOrderId, setEditingInvoiceOrderId] = useState(null);
	const [statementInvoicePopoverPos, setStatementInvoicePopoverPos] = useState(null); // { top, left, arrowLeft }
	const contractInputRef = useRef(null);
	const contractPopoverAnchorRef = useRef(null);
	const statementContractPopoverPanelRef = useRef(null);
	const invoiceInputRef = useRef(null);
	const invoicePopoverAnchorRef = useRef(null);
	const statementInvoicePopoverPanelRef = useRef(null);

	const closeStatementContractPopover = useCallback(() => {
		setEditingContractOrderId(null);
		setStatementContractPopoverPos(null);
	}, []);
	const closeStatementInvoicePopover = useCallback(() => {
		setEditingInvoiceOrderId(null);
		setStatementInvoicePopoverPos(null);
	}, []);

	useEffect(() => {
		if (!show) return;
		setStep('range');
		setPreviewScale(1);
		setToolbarExpanded(false);
		setToolbarHovering(false);
		setContractDraftByOrderId({});
		setContractSaveErrorByOrderId({});
		setInvoiceDraftByOrderId({});
		setInvoiceSaveErrorByOrderId({});
		setSavingOrderId(null);
		setEditingContractOrderId(null);
		setStatementContractPopoverPos(null);
		setEditingInvoiceOrderId(null);
		setStatementInvoicePopoverPos(null);
		setPreviewPayload(null);
		setPreviewError('');
		setPreviewLoading(false);
		const def = snapshotChartIndex !== null && snapshotChartIndex !== undefined && snapshotChartIndex >= 0 ? [snapshotChartIndex] : [0, 1, 2];
		setSelectedBuckets(new Set(def));
	}, [show, snapshotChartIndex]);

	useEffect(() => {
		if (!editingContractOrderId) return;
		let cancelled = false;
		const id = window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				if (!cancelled) contractInputRef.current?.focus?.();
			});
		});
		return () => {
			cancelled = true;
			window.cancelAnimationFrame(id);
		};
	}, [editingContractOrderId]);

	useLayoutEffect(() => {
		if (!editingContractOrderId) {
			setStatementContractPopoverPos(null);
			return;
		}
		// 以“补充合同”按钮作为弹层锚点
		const anchor = contractPopoverAnchorRef.current;
		if (!anchor) return;

		const POPOVER_W_FALLBACK = Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 16 : 320);
		/** 首次布局前尚无面板 DOM；过大估算会误判「下方放不下」→ 翻到顶部并被 clamp 到 y=8，出现截图中的严重错位 */
		const POPOVER_H_FALLBACK = 150;
		const MARGIN = 8;

		const place = () => {
			// 读取锚点与弹层尺寸，计算弹层坐标
			const r = anchor.getBoundingClientRect();
			const panel = statementContractPopoverPanelRef.current;
			const popoverH = panel?.offsetHeight > 0 ? panel.offsetHeight : POPOVER_H_FALLBACK;
			const boxW = panel?.offsetWidth > 0 ? panel.offsetWidth : POPOVER_W_FALLBACK;
			let top = r.bottom + MARGIN;
			let left = r.left;
			// 下方放不下时，改为放到按钮上方
			if (top + popoverH > window.innerHeight - MARGIN) {
				top = Math.max(MARGIN, r.top - popoverH - MARGIN);
			}
			// 防止左右越界
			if (left + boxW > window.innerWidth - MARGIN) {
				left = Math.max(MARGIN, window.innerWidth - boxW - MARGIN);
			}
			if (left < MARGIN) left = MARGIN;
			// 小三角箭头相对弹层容器的 X 偏移
			const anchorCenterX = r.left + r.width / 2;
			const arrowLeft = Math.max(14, Math.min(boxW - 14, anchorCenterX - left));
			setStatementContractPopoverPos({ top, left, arrowLeft });
		};

		// 首次打开 + 下一微任务/帧再算一次，避免初次尺寸未稳定导致错位
		place();
		queueMicrotask(() => place());
		// 弹层所在滚动容器一旦滚动就关闭（避免“漂移感”）
		const scrollRoots = getScrollableAncestors(anchor);
		const onScrollDismiss = () => closeStatementContractPopover();
		scrollRoots.forEach((root) => root.addEventListener('scroll', onScrollDismiss, { passive: true }));
		window.addEventListener('resize', place);
		let rafId = window.requestAnimationFrame(() => {
			place();
			rafId = window.requestAnimationFrame(() => place());
		});
		return () => {
			window.cancelAnimationFrame(rafId);
			scrollRoots.forEach((root) => root.removeEventListener('scroll', onScrollDismiss));
			window.removeEventListener('resize', place);
		};
	}, [editingContractOrderId, previewScale, closeStatementContractPopover]);

	useEffect(() => {
		if (!editingContractOrderId) return;
		const onMouseDown = (e) => {
			const panel = statementContractPopoverPanelRef.current;
			const anchor = contractPopoverAnchorRef.current;
			if (panel?.contains(e.target)) return;
			if (anchor?.contains(e.target)) return;
			closeStatementContractPopover();
		};
		document.addEventListener('mousedown', onMouseDown);
		return () => document.removeEventListener('mousedown', onMouseDown);
	}, [editingContractOrderId, closeStatementContractPopover]);

	useEffect(() => {
		if (!editingInvoiceOrderId) return;
		let cancelled = false;
		const id = window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				if (!cancelled) invoiceInputRef.current?.focus?.();
			});
		});
		return () => {
			cancelled = true;
			window.cancelAnimationFrame(id);
		};
	}, [editingInvoiceOrderId]);

	useLayoutEffect(() => {
		if (!editingInvoiceOrderId) {
			setStatementInvoicePopoverPos(null);
			return;
		}
		// 以“补充发票”按钮作为弹层锚点
		const anchor = invoicePopoverAnchorRef.current;
		if (!anchor) return;

		const POPOVER_W_FALLBACK = Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 16 : 320);
		const POPOVER_H_FALLBACK = 150;
		const MARGIN = 8;

		const place = () => {
			// 读取锚点与弹层尺寸，计算弹层坐标
			const r = anchor.getBoundingClientRect();
			const panel = statementInvoicePopoverPanelRef.current;
			const popoverH = panel?.offsetHeight > 0 ? panel.offsetHeight : POPOVER_H_FALLBACK;
			const boxW = panel?.offsetWidth > 0 ? panel.offsetWidth : POPOVER_W_FALLBACK;
			let top = r.bottom + MARGIN;
			let left = r.left;
			// 下方放不下时，改为放到按钮上方
			if (top + popoverH > window.innerHeight - MARGIN) {
				top = Math.max(MARGIN, r.top - popoverH - MARGIN);
			}
			// 防止左右越界
			if (left + boxW > window.innerWidth - MARGIN) {
				left = Math.max(MARGIN, window.innerWidth - boxW - MARGIN);
			}
			if (left < MARGIN) left = MARGIN;
			// 小三角箭头相对弹层容器的 X 偏移
			const anchorCenterX = r.left + r.width / 2;
			const arrowLeft = Math.max(14, Math.min(boxW - 14, anchorCenterX - left));
			setStatementInvoicePopoverPos({ top, left, arrowLeft });
		};

		// 首次打开 + 下一微任务/帧再算一次，避免初次尺寸未稳定导致错位
		place();
		queueMicrotask(() => place());
		// 弹层所在滚动容器一旦滚动就关闭（避免“漂移感”）
		const scrollRoots = getScrollableAncestors(anchor);
		const onScrollDismiss = () => closeStatementInvoicePopover();
		scrollRoots.forEach((root) => root.addEventListener('scroll', onScrollDismiss, { passive: true }));
		window.addEventListener('resize', place);
		let rafId = window.requestAnimationFrame(() => {
			place();
			rafId = window.requestAnimationFrame(() => place());
		});
		return () => {
			window.cancelAnimationFrame(rafId);
			scrollRoots.forEach((root) => root.removeEventListener('scroll', onScrollDismiss));
			window.removeEventListener('resize', place);
		};
	}, [editingInvoiceOrderId, previewScale, closeStatementInvoicePopover]);

	useEffect(() => {
		if (!editingInvoiceOrderId) return;
		const onMouseDown = (e) => {
			const panel = statementInvoicePopoverPanelRef.current;
			const anchor = invoicePopoverAnchorRef.current;
			if (panel?.contains(e.target)) return;
			if (anchor?.contains(e.target)) return;
			closeStatementInvoicePopover();
		};
		document.addEventListener('mousedown', onMouseDown);
		return () => document.removeEventListener('mousedown', onMouseDown);
	}, [editingInvoiceOrderId, closeStatementInvoicePopover]);

	const toggleBucket = (idx) => {
		setSelectedBuckets((prev) => {
			const next = new Set(prev);
			if (next.has(idx)) next.delete(idx);
			else next.add(idx);
			return next;
		});
	};

	const loadPreview = useCallback(async () => {
		if (!seedOrder?.customerId || selectedBuckets.size === 0) return;
		const reqId = ++previewReqRef.current;
		setPreviewLoading(true);
		setPreviewError('');
		try {
			const data = await previewStatementPdf({
				customerId: String(seedOrder.customerId),
				bucketIndices: [...selectedBuckets],
			}).unwrap();
			if (reqId !== previewReqRef.current) return;
			setPreviewPayload(data);
		} catch (e) {
			if (reqId !== previewReqRef.current) return;
			setPreviewPayload(null);
			setPreviewError(e?.data?.message || e?.message || '预览加载失败');
		} finally {
			if (reqId === previewReqRef.current) setPreviewLoading(false);
		}
	}, [previewStatementPdf, seedOrder, selectedBuckets]);

	useEffect(() => {
		if (!show || step !== 'preview') {
			previewReqRef.current += 1;
			return;
		}
		loadPreview();
	}, [show, step, loadPreview]);

	const lines = useMemo(() => {
		const raw = previewPayload?.lines;
		if (!Array.isArray(raw)) return [];
		return raw.map((line) => ({
			...line,
			order_id: line.orderMongoId,
			ar_id: line.arId,
			title: line.paymentStage,
			start_time: line.startDate,
			expire_time: line.dueDate,
		}));
	}, [previewPayload]);

	const previewCustomerPhone = String(previewPayload?.customerPhone ?? '').trim();
	const previewCustomerEmail = String(previewPayload?.customerEmail ?? '').trim();
	const previewCustomerAddress = String(previewPayload?.customerAddress ?? '').trim();
	const previewCustomerOrgLine = String(previewPayload?.customerOrganizationLine ?? '').trim();
	const previewSaleRepEntries = Array.isArray(previewPayload?.salesRepEntries) ? previewPayload.salesRepEntries : [];
	const previewCustomerDisplayName = String(previewPayload?.customerName ?? '').trim() || seedOrder?.customerName;

	const contractPopoverOrderLabel = useMemo(() => {
		if (!editingContractOrderId) return '';
		const hit = lines.find((r) => String(r.order_id) === editingContractOrderId);
		return hit?.orderId ?? editingContractOrderId;
	}, [lines, editingContractOrderId]);
	const invoicePopoverOrderLabel = useMemo(() => {
		if (!editingInvoiceOrderId) return '';
		const hit = lines.find((r) => String(r.order_id) === editingInvoiceOrderId);
		return hit?.orderId ?? editingInvoiceOrderId;
	}, [lines, editingInvoiceOrderId]);

	const totalAR = useMemo(() => lines.reduce((s, r) => s + (Number(r.accountsReceivable) || 0), 0), [lines]);

	const previewAsOfDate = step === 'preview' ? String(previewPayload?.asOfDate ?? '') : '';

	const statementPdfPreviewReady = useMemo(() => {
		if (step !== 'preview') return true;
		if (statementPdfConfigLoading || previewLoading || previewError) return false;
		return Boolean(previewPayload);
	}, [step, statementPdfConfigLoading, previewLoading, previewError, previewPayload]);

	const formatMoneyPlain = useCallback((n) => {
		const v = Number(n) || 0;
		return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
	}, []);

	const formatMoney = useCallback((n) => {
		return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
	}, []);
	const previewTheme = useMemo(
		() => ({
			modalBackground: isDark ? '#0b1220' : '#f8fafc',
			modalBorder: isDark ? '#334155' : '#dee2e6',
			modalText: isDark ? '#e2e8f0' : '#212529',
			stageBackground: isDark ? 'linear-gradient(180deg, #0f172a 0%, #111827 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
			stageBorder: isDark ? '#334155' : '#e2e8f0',
			toolbarBackground: isDark ? 'rgba(15, 23, 42, 0.56)' : 'rgba(248, 250, 252, 0.62)',
			toolbarBorder: isDark ? 'rgba(71, 85, 105, 0.55)' : 'rgba(148, 163, 184, 0.35)',
			labelColor: isDark ? '#e2e8f0' : '#0f172a',
			cardBorder: isDark ? '#475569' : '#e5e7eb',
			cardShadow: isDark ? '0 10px 24px rgba(2,6,23,0.45)' : '0 10px 24px rgba(15,23,42,0.08)',
			cardHoverShadow: isDark ? '0 14px 30px rgba(2,6,23,0.55)' : '0 14px 28px rgba(15,23,42,0.11)',
		}),
		[isDark],
	);

	// 表格外框与单元格 padding 等同 PDF：见 STATEMENT_PDF_PREVIEW_SCOPED_CSS；此处仅 textAlign / 合同列换行等覆盖
	const monoEnd = { fontVariantNumeric: 'tabular-nums' };

	const handleGenerate = () => setStep('preview');

	const handleClose = () => onHide?.();

	const handleSaveOrderContracts = async (orderMongoId) => {
		const idKey = orderMongoId == null ? '' : String(orderMongoId);
		if (!idKey) return;
		const trimmed = String(contractDraftByOrderId[idKey] ?? '').trim();
		if (!trimmed) {
			setContractSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: '请输入合同编号' }));
			return;
		}
		setSavingOrderId(idKey);
		setContractSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: '' }));
		try {
			await addOrderContracts({ _id: idKey, contractNumbers: [trimmed] }).unwrap();
			setContractDraftByOrderId((prev) => {
				const next = { ...prev };
				delete next[idKey];
				return next;
			});
			closeStatementContractPopover();
			await loadPreview();
		} catch (e) {
			const msg = e?.data?.message || e?.message || '保存失败';
			setContractSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: msg }));
		} finally {
			setSavingOrderId(null);
		}
	};

	const handleSaveOrderInvoices = async (orderMongoId) => {
		const idKey = orderMongoId == null ? '' : String(orderMongoId);
		if (!idKey) return;
		const trimmed = String(invoiceDraftByOrderId[idKey] ?? '').trim();
		if (!trimmed) {
			setInvoiceSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: '请输入发票号码' }));
			return;
		}
		setSavingOrderId(idKey);
		setInvoiceSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: '' }));
		try {
			await addOrderInvoices({ _id: idKey, invoiceNumbers: [trimmed] }).unwrap();
			setInvoiceDraftByOrderId((prev) => {
				const next = { ...prev };
				delete next[idKey];
				return next;
			});
			closeStatementInvoicePopover();
			await loadPreview();
		} catch (e) {
			const msg = e?.data?.message || e?.message || '保存失败';
			setInvoiceSaveErrorByOrderId((prev) => ({ ...prev, [idKey]: msg }));
		} finally {
			setSavingOrderId(null);
		}
	};

	const handleDownloadPdf = async () => {
		try {
			if (!seedOrder?.customerId) {
				alert('缺少客户编号，无法生成 PDF');
				return;
			}
			setPdfGenerating(true);
			const bucketIndices = [...selectedBuckets];
			const payload = {
				customerId: String(seedOrder.customerId),
				bucketIndices,
			};

			const createResult = await createStatementPdfJob(payload).unwrap();
			const jobId = createResult?.jobId;
			if (!jobId) {
				throw new Error('未返回任务ID');
			}

			const pollIntervalMs = 2000;
			const maxWaitMs = 90000; // 最长约 90 秒
			const deadline = Date.now() + maxWaitMs;

			const handleJobStatus = async (statusResult) => {
				if (statusResult?.status === 'done') {
					const { blob, fileName } = await downloadStatementPdfFile(jobId).unwrap();
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					link.download = fileName || 'statement.pdf';
					document.body.appendChild(link);
					link.click();
					link.remove();
					window.URL.revokeObjectURL(url);
					return true;
				}
				if (statusResult?.status === 'failed') {
					throw new Error(statusResult.error || '任务失败');
				}
				if (statusResult?.status === 'expired') {
					throw new Error(statusResult.error || '下载链接已过期，请重新生成');
				}
				return false;
			};

			while (Date.now() <= deadline) {
				const statusResult = await triggerGetJobStatus(jobId).unwrap();
				if (await handleJobStatus(statusResult)) return;
				if (Date.now() + pollIntervalMs > deadline) break;
				await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
			}

			// 截止前后边界再查一次，避免竞态导致误报超时
			const finalStatus = await triggerGetJobStatus(jobId).unwrap();
			if (await handleJobStatus(finalStatus)) return;
			throw new Error('生成超时，请稍后重试');
		} catch (e) {
			const msg = e?.data?.message || e?.message || '未知错误';
			alert(`PDF 生成失败：${msg}`);
		} finally {
			setPdfGenerating(false);
		}
	};

	const renderBucketBadge = (idx) => {
		const label = CHART_LABELS[idx];
		const active = selectedBuckets.has(idx);
		const severityBg = chartBucketSeverityColors[idx] ?? '#C41E3A';
		return (
			<span
				key={`bucket-${idx}-${label}`}
				className={`statement-pdf-range-bucket badge rounded-pill px-3 py-2 fw-normal cursor-pointer user-select-none ${active ? 'statement-pdf-bucket--on' : 'text-bg-secondary'}`}
				style={{
					cursor: 'pointer',
					opacity: active ? 1 : 0.65,
					...(active ? { '--spf-bucket-bg': severityBg } : {}),
				}}
				onClick={() => toggleBucket(idx)}
				role='button'
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						toggleBucket(idx);
					}
				}}
			>
				{label}
			</span>
		);
	};

	const previewModalHeaderStyle =
		step === 'preview'
			? {
					background: previewTheme.modalBackground,
					borderBottom: `1px solid ${previewTheme.modalBorder}`,
					color: previewTheme.modalText,
				}
			: undefined;
	const previewModalBodyStyle =
		step === 'preview'
			? {
					background: previewTheme.modalBackground,
					color: previewTheme.modalText,
					padding: 0,
					overflow: 'hidden',
				}
			: undefined;
	const previewModalFooterStyle =
		step === 'preview'
			? {
					background: previewTheme.modalBackground,
					borderTop: `1px solid ${previewTheme.modalBorder}`,
				}
			: undefined;

	return (
		<Modal
			show={show}
			onHide={handleClose}
			size={step === 'preview' ? 'xl' : undefined}
			centered
			scrollable={step !== 'preview'}
			backdrop='static'
			enforceFocus={!(step === 'preview' && (editingContractOrderId || editingInvoiceOrderId))}
			contentClassName={isDark ? 'statement-preview-modal-content-dark' : undefined}
		>
			<style>{`
				.statement-preview-modal-content-dark {
					background: #0b1220 !important;
					border: 1px solid #334155 !important;
					color: #e2e8f0 !important;
				}
				.statement-preview-modal-content-dark .modal-header,
				.statement-preview-modal-content-dark .modal-body,
				.statement-preview-modal-content-dark .modal-footer {
					background: #0b1220 !important;
					color: #e2e8f0 !important;
					border-color: #334155 !important;
				}
				.statement-preview-modal-content-dark .modal-title {
					color: #e2e8f0 !important;
				}
				.statement-preview-modal-content-dark .btn-close {
					filter: invert(1) grayscale(1) brightness(1.2);
				}
			`}</style>
			<Modal.Header closeButton style={previewModalHeaderStyle}>
				<Modal.Title>{step === 'range' ? '请选择统计区间' : '客户对账单预览'}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={previewModalBodyStyle}>
				{step === 'range' && (
					<>
						<style>{`
							/* 未选中与选中共用 1px 边框占位，避免切换时盒模型抖动 */
							.statement-pdf-range-bucket.badge {
								box-sizing: border-box !important;
								border: 1px solid transparent !important;
								display: inline-flex !important;
								align-items: center !important;
							}
							.statement-pdf-range-bucket.statement-pdf-bucket--on.badge {
								background-color: var(--spf-bucket-bg) !important;
								color: #fff !important;
								border-color: var(--spf-bucket-bg) !important;
								-webkit-text-fill-color: #fff !important;
							}
						`}</style>
						<p className='text-muted small mb-2'>
							将汇总客户「<strong>{seedOrder?.customerName}</strong>」在下列区间内的全部应收账款。
						</p>
						<Form.Label className='fw-semibold small mb-2 d-block'>统计区间</Form.Label>
						<div className='mb-3'>
							<div className='small fw-semibold text-danger mb-1'>超期</div>
							<div className='d-flex flex-wrap gap-2'>{Array.from({ length: CHART_BUCKETS_RECENT_START_INDEX }, (_, i) => renderBucketBadge(i))}</div>
						</div>
						<div className='mb-3'>
							<div className='small fw-semibold text-primary mb-1'>近期</div>
							<div className='d-flex flex-wrap gap-2'>
								{CHART_LABELS.slice(CHART_BUCKETS_RECENT_START_INDEX).map((_, off) => renderBucketBadge(CHART_BUCKETS_RECENT_START_INDEX + off))}
							</div>
						</div>
						<div className='rounded border border-200 small mb-0 p-2 bg-100 dark__bg-1000 text-body'>
							当前预计包含 <strong>{lines.length}</strong> 条应收账款 · 合计 <strong>{formatMoney(totalAR)}</strong>
						</div>
					</>
				)}
				{step === 'preview' && (
					<div
						className='statement-preview-stage overflow-auto pt-0 pb-3 px-2 rounded'
						style={{
							height: '72vh',
							background: previewTheme.stageBackground,
							border: `1px solid ${previewTheme.stageBorder}`,
						}}
						onMouseMove={(e) => {
							const rect = e.currentTarget.getBoundingClientRect();
							const nearTop = e.clientY - rect.top <= 22;
							if (nearTop) {
								if (!toolbarExpanded) setToolbarExpanded(true);
								return;
							}
							if (!toolbarHovering && toolbarExpanded) {
								setToolbarExpanded(false);
							}
						}}
						onMouseLeave={() => {
							setToolbarHovering(false);
							setToolbarExpanded(false);
						}}
					>
						<style>{`
							.statement-preview-toolbar-trigger {
								position: sticky;
								top: 0;
								z-index: 6;
								height: 10px;
								margin-bottom: -10px;
							}
							.statement-preview-toolbar {
								position: sticky;
								top: 0;
								z-index: 5;
								background: rgba(248, 250, 252, 0.62);
								backdrop-filter: blur(4px);
								border-bottom: 1px solid rgba(148, 163, 184, 0.35);
								padding: 8px 0 10px;
								margin-bottom: 12px;
								transform: translateY(-120%);
								opacity: 0;
								pointer-events: none;
								transition: transform 0.18s ease, opacity 0.18s ease;
							}
							.statement-preview-toolbar.statement-preview-toolbar--expanded {
								transform: translateY(0);
								opacity: 1;
								pointer-events: auto;
							}
							.statement-preview-page-card {
								transition: box-shadow 0.18s ease, transform 0.18s ease;
							}
							.statement-preview-page-card:hover {
								box-shadow: ${previewTheme.cardHoverShadow} !important;
							}
						`}</style>
						{previewError ? (
							<div className='d-flex justify-content-center align-items-center text-center px-3' style={{ minHeight: '52vh', color: previewTheme.labelColor }} role='alert'>
								<span className='small'>预览加载失败：{previewError}</span>
							</div>
						) : !statementPdfPreviewReady ? (
							<div
								className='d-flex flex-column justify-content-center align-items-center gap-2 text-center px-3'
								style={{ minHeight: '52vh', color: previewTheme.labelColor }}
								role='status'
								aria-live='polite'
							>
								<Spinner animation='border' variant={isDark ? 'light' : 'dark'} />
								<span className='small'>正在加载对账单…</span>
							</div>
						) : (
							<>
								<div className='statement-preview-toolbar-trigger' />
								<div
									className={`statement-preview-toolbar d-flex justify-content-end align-items-center gap-3 ${toolbarExpanded ? 'statement-preview-toolbar--expanded' : ''}`}
									style={{
										background: previewTheme.toolbarBackground,
										borderBottom: `1px solid ${previewTheme.toolbarBorder}`,
									}}
									onMouseEnter={() => {
										setToolbarHovering(true);
										setToolbarExpanded(true);
									}}
									onMouseLeave={() => {
										setToolbarHovering(false);
										setToolbarExpanded(false);
									}}
								>
									<span
										className='fw-bold'
										style={{
											color: previewTheme.labelColor,
											fontSize: '16px',
											fontWeight: 800,
											letterSpacing: '0.45px',
											lineHeight: 1,
											textShadow: isDark ? '0 0 10px rgba(148,163,184,0.25)' : 'none',
										}}
									>
										缩放
									</span>
									<div className='d-flex align-items-center gap-2' style={{ minWidth: '170px' }}>
										<Form.Range
											min={70}
											max={100}
											step={1}
											value={170 - Math.round(previewScale * 100)}
											onChange={(e) => setPreviewScale((170 - Number(e.target.value)) / 100)}
											aria-label='预览缩放'
											style={{ margin: 0 }}
										/>
										<span className='fw-semibold' style={{ minWidth: '44px', color: previewTheme.labelColor }}>
											{Math.round(previewScale * 100)}%
										</span>
									</div>
								</div>
								<div className='position-relative px-2 pb-4'>
									<div
										className='statement-preview-page-card statement-pdf-react-preview mx-auto'
										style={{
											width: '210mm',
											maxWidth: '100%',
											border: `1px solid ${previewTheme.cardBorder}`,
											boxShadow: previewTheme.cardShadow,
											borderRadius: '8px',
											transform: `scale(${previewScale})`,
											transformOrigin: 'top center',
											position: 'relative',
											zIndex: 1,
											overflow: 'auto',
											background: '#fff',
											color: '#111',
										}}
									>
										<style>{STATEMENT_PDF_FONT_FACE_CSS}</style>
										<style>{STATEMENT_PDF_PREVIEW_SCOPED_CSS}</style>
										<div className='page'>
											<div className='row'>
												<div className='box' style={{ flex: tpl.headerLeftFlex }}>
													<div style={{ fontWeight: 700, fontSize: tpl.companyNameFontSize }}>{String(tpl.companyInfo.name ?? '').trim()}</div>
													{[tpl.companyInfo.line1, tpl.companyInfo.line2, tpl.companyInfo.line3, tpl.companyInfo.contactLine1, tpl.companyInfo.contactLine2]
														.map((item) => String(item ?? '').trim())
														.filter(Boolean)
														.map((line, idx) => (
															<div key={`${idx}-${line}`}>{line}</div>
														))}
												</div>
												<div style={{ flex: tpl.headerRightFlex, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
													{tenantLogoUrl ? (
														<img src={tenantLogoUrl} alt='tenant-logo' style={{ maxWidth: tpl.logoMaxWidth, maxHeight: tpl.logoMaxHeight, objectFit: 'contain' }} />
													) : (
														<div style={{ fontSize: 42, fontWeight: 700, color: tpl.logoFallbackColor }}>{tenantDisplayName}</div>
													)}
												</div>
											</div>

											<div className='h1'>{tpl.text.documentTitle}</div>
											<div style={{ margin: '-2px 0 10px', color: '#333', fontSize: '14px', fontWeight: 700 }}>
												{tpl.text.asOfDate}：{previewAsOfDate}
											</div>

											<div className='box' style={{ padding: 0 }}>
												<div className='row' style={{ gap: 0 }}>
													<div style={{ flex: 7, padding: '8px', borderRight: '1px solid #111', minWidth: 0, whiteSpace: 'normal' }}>
														<div
															style={{
																fontWeight: 700,
																margin: `0 0 ${STATEMENT_PDF_TEMPLATE.customerSectionHeadingMarginBottom}`,
															}}
														>
															{tpl.text.customerTitle || tpl.text.billTo}
														</div>
														<div style={{ margin: `0 0 ${STATEMENT_PDF_TEMPLATE.customerLegalNameMarginBottom}` }}>{previewCustomerDisplayName}</div>
														{previewCustomerPhone ? (
															<div className='metaLine'>
																{tpl.text.customerPhoneLabel}：{previewCustomerPhone}
															</div>
														) : null}
														{previewCustomerEmail ? (
															<div className='metaLine'>
																{tpl.text.customerEmailLabel}：{previewCustomerEmail}
															</div>
														) : null}
														{previewCustomerAddress ? (
															<div className='metaLine'>
																{tpl.text.customerAddressLabel}：{previewCustomerAddress}
															</div>
														) : null}
														{previewCustomerOrgLine ? (
															<div className='metaLine'>
																{tpl.text.customerOrganizationLabel}：{previewCustomerOrgLine}
															</div>
														) : null}
													</div>
													<div style={{ flex: 5, padding: '8px', minWidth: 0, whiteSpace: 'normal' }}>
														<div
															style={{
																fontWeight: 700,
																margin: `0 0 ${STATEMENT_PDF_TEMPLATE.customerSectionHeadingMarginBottom}`,
															}}
														>
															{tpl.text.salesRepLabel}
														</div>
														{previewSaleRepEntries.length === 0 ? (
															<div className='metaLine'>—</div>
														) : (
															previewSaleRepEntries.map((rep) => (
																<div key={rep.name} style={{ marginBottom: 8 }}>
																	<div className='metaLine'>
																		<b>{rep.name}</b>
																	</div>
																	{rep.phone ? (
																		<div className='metaLine'>
																			{tpl.text.customerPhoneLabel}：{rep.phone}
																		</div>
																	) : null}
																	{rep.email ? (
																		<div className='metaLine'>
																			{tpl.text.customerEmailLabel}：{rep.email}
																		</div>
																	) : null}
																</div>
															))
														)}
													</div>
												</div>
											</div>

											<div className='tableWrap'>
												<table>
													<thead>
														<tr>
															{tpl.text.tableHeaders.map((head, idx) => (
																<th key={`${idx}-${head}`}>{head}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{lines.length === 0 ? (
															<tr>
																<td colSpan={STATEMENT_PDF_TABLE_COLUMN_COUNT} style={{ textAlign: 'center', color: tpl.emptyCellColor, padding: tpl.emptyCellPadding }}>
																	{tpl.text.emptyLine}
																</td>
															</tr>
														) : (
															lines.map((row, i) => {
																const oid = row.order_id == null ? '' : String(row.order_id);
																const missingContract = isContractNumberMissing(row);
																const missingInvoice = isInvoiceNumberMissing(row);
																const contractDisplay = missingContract ? '—' : row.contractNumber;
																const invoiceDisplay = missingInvoice ? '—' : row.invoiceNumber;
																const contractDisplayLines = splitDisplayNumbers(contractDisplay);
																const invoiceDisplayLines = splitDisplayNumbers(invoiceDisplay);
																const startDateText = dateTextFromBackendField(row.start_time);
																const expireDateText = dateTextFromBackendField(row.expire_time);
																const overdueDay = Number(row.overdueDay) || 0;
																const overdueText = overdueDay < 0 ? String(Math.abs(overdueDay)) : '0';
																return (
																	<tr key={`${row.ar_id ?? i}_${row.order_id}`}>
																		<td style={{ textAlign: 'center', whiteSpace: 'normal' }}>
																			<div className='d-flex align-items-center justify-content-between gap-2 flex-wrap'>
																				<div style={{ textAlign: 'left' }}>
																					{contractDisplayLines.map((line, idx) => (
																						<div
																							key={`${oid || row.orderId || i}-contract-${idx}`}
																							style={missingContract ? { color: '#64748b', fontSize: '11px', letterSpacing: '0.02em' } : undefined}
																						>
																							{line}
																						</div>
																					))}
																				</div>
																				<Button
																					type='button'
																					size='sm'
																					variant='outline-secondary'
																					className='py-0 px-2 fs-10'
																					onClick={(e) => {
																						e.preventDefault();
																						contractPopoverAnchorRef.current = e.currentTarget;
																						setEditingContractOrderId(oid);
																					}}
																					disabled={!oid}
																					aria-label={`为订单 ${row.orderId} 录入合同`}
																					aria-haspopup='dialog'
																					aria-expanded={editingContractOrderId === oid}
																				>
																					补充
																				</Button>
																			</div>
																		</td>
																		<td style={{ textAlign: 'center', whiteSpace: 'normal' }}>
																			<div className='d-flex align-items-center justify-content-between gap-2 flex-wrap'>
																				<div style={{ textAlign: 'left' }}>
																					{invoiceDisplayLines.map((line, idx) => (
																						<div
																							key={`${oid || row.orderId || i}-invoice-${idx}`}
																							style={missingInvoice ? { color: '#64748b', fontSize: '11px', letterSpacing: '0.02em' } : undefined}
																						>
																							{line}
																						</div>
																					))}
																				</div>
																				<Button
																					type='button'
																					size='sm'
																					variant='outline-secondary'
																					className='py-0 px-2 fs-10'
																					onClick={(e) => {
																						e.preventDefault();
																						invoicePopoverAnchorRef.current = e.currentTarget;
																						setEditingInvoiceOrderId(oid);
																					}}
																					disabled={!oid}
																					aria-label={`为订单 ${row.orderId} 录入发票`}
																					aria-haspopup='dialog'
																					aria-expanded={editingInvoiceOrderId === oid}
																				>
																					补充
																				</Button>
																			</div>
																		</td>
																		<td style={{ textAlign: 'center' }}>
																			{row.orderId}
																			<div style={{ fontSize: tpl.tableCellSubFontSize, color: tpl.tableCellSubColor }}>{paymentStageWithTermDays(row)}</div>
																		</td>
																		<td style={{ textAlign: 'center' }}>
																			<div>{startDateText}</div>
																			<div>{expireDateText}</div>
																		</td>
																		<td style={{ textAlign: 'center' }}>{overdueText}</td>
																		<td style={{ textAlign: 'center' }}>{String(row.currency ?? tpl.currencyCode ?? '').trim() || 'CNY'}</td>
																		<td style={{ textAlign: 'right', ...monoEnd }}>{formatMoneyPlain(row.accountsReceivable)}</td>
																	</tr>
																);
															})
														)}
													</tbody>
												</table>
											</div>

											<div className='totalRow'>
												<div className='label'>{tpl.text.totalLabel}</div>
												<div className='currency'>{tpl.currencyCode}</div>
												<div className='amount' style={monoEnd}>
													{formatMoneyPlain(totalAR)}
												</div>
											</div>

											<div className='box small' style={{ marginTop: 20 }}>
												<b>{tpl.text.remarkLabel}</b>
												{tpl.text.remarkText}
											</div>

											<div className='box' style={{ padding: 0, marginTop: 20 }}>
												<div className='sectionTitle'>{tpl.text.paymentTitle}</div>
												<div className='footerGrid'>
													<div className='footerCol small'>
														<div>
															<b>{tpl.text.wire}</b>
														</div>
														{String(tpl.text.wireEnterpriseName ?? '').trim() ? <div>企业名称：{String(tpl.text.wireEnterpriseName).trim()}</div> : null}
														{String(tpl.text.wireTaxId ?? '').trim() ? <div>税号：{String(tpl.text.wireTaxId).trim()}</div> : null}
														{String(tpl.text.wireAddress ?? '').trim() ? <div>地址：{String(tpl.text.wireAddress).trim()}</div> : null}
														{String(tpl.text.wirePhone ?? '').trim() ? <div>电话：{String(tpl.text.wirePhone).trim()}</div> : null}
														{String(tpl.text.wireBank ?? '').trim() ? <div>{String(tpl.text.wireBank).trim()}</div> : null}
														{String(tpl.text.wireAccount ?? '').trim() ? <div>{String(tpl.text.wireAccount).trim()}</div> : null}
														{String(tpl.text.wireEnterpriseName ?? '').trim() ? null : <div>户名：{tpl.companyInfo.name}</div>}
													</div>
													<div className='footerCol small'>
														<div>
															<b>{tpl.text.check}</b>
														</div>
														{String(tpl.text.checkMail ?? '').trim() ? <div>{String(tpl.text.checkMail).trim()}</div> : null}
														{String(tpl.text.checkExpress ?? '').trim() ? <div>{String(tpl.text.checkExpress).trim()}</div> : null}
														{String(tpl.text.checkLine3 ?? '').trim() ? <div>{String(tpl.text.checkLine3).trim()}</div> : null}
														{String(tpl.text.checkLine4 ?? '').trim() ? <div>{String(tpl.text.checkLine4).trim()}</div> : null}
													</div>
												</div>
											</div>

											<div
												className='statementPdfPageFooter'
												style={{
													borderTop: `1px solid ${previewTheme.toolbarBorder}`,
													color: '#0a0a0a',
													fontWeight: 600,
												}}
											>
												第 1 页 / 共 1 页（预览；导出 PDF 为真实分页）
											</div>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				)}
			</Modal.Body>
			{typeof document !== 'undefined' &&
				step === 'preview' &&
				editingContractOrderId &&
				statementContractPopoverPos &&
				createPortal(
					<div
						ref={statementContractPopoverPanelRef}
						style={{
							position: 'fixed',
							top: statementContractPopoverPos.top,
							left: statementContractPopoverPos.left,
							zIndex: 1080,
							pointerEvents: 'auto',
						}}
					>
						<div
							id='statement-pdf-contract-popover'
							className='shadow-sm position-relative'
							style={{
								maxWidth: 'min(320px, calc(100vw - 16px))',
								margin: 0,
								borderRadius: '6px',
								border: `1px solid ${isDark ? '#475569' : '#dee2e6'}`,
								backgroundColor: isDark ? '#1e293b' : '#fff',
								...(isDark ? { backgroundColor: '#1e293b', borderColor: '#475569', color: '#e2e8f0' } : {}),
							}}
						>
							<div
								style={{
									position: 'absolute',
									top: -7,
									left: statementContractPopoverPos?.arrowLeft ?? 24,
									transform: 'translateX(-50%)',
									width: 14,
									height: 8,
									backgroundColor: isDark ? '#1e293b' : '#fff',
									clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
									pointerEvents: 'none',
									zIndex: 2,
								}}
							/>
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: statementContractPopoverPos?.arrowLeft ?? 24,
									transform: 'translateX(-50%)',
									width: 16,
									height: 1,
									backgroundColor: isDark ? '#1e293b' : '#fff',
									pointerEvents: 'none',
									zIndex: 2,
								}}
							/>
							<div className={isDark ? 'text-light' : ''} style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 5, paddingBottom: 10 }}>
								<div className='d-flex align-items-center gap-2' style={{ marginBottom: '8px', minHeight: '1.25rem' }}>
									<div
										className={`flex-grow-1 min-w-0 text-break lh-sm ${isDark ? '' : 'text-muted'}`}
										style={{
											fontSize: '11px',
											fontWeight: 500,
											letterSpacing: '0.02em',
											paddingRight: '6px',
											lineHeight: 1.35,
											...(isDark ? { color: '#94a3b8' } : {}),
										}}
									>
										订单 {contractPopoverOrderLabel} · 合同编号
									</div>
								</div>
								<div className='d-flex flex-column gap-2'>
									<div className='d-flex gap-2 align-items-center'>
										<Form.Control
											ref={contractInputRef}
											size='sm'
											className={`fs-10 ${isDark ? 'bg-dark text-white border-secondary' : ''}`}
											value={contractDraftByOrderId[editingContractOrderId] ?? ''}
											onChange={(e) =>
												setContractDraftByOrderId((prev) => ({
													...prev,
													[editingContractOrderId]: e.target.value,
												}))
											}
											placeholder='请输入合同编号'
											disabled={savingOrderId === editingContractOrderId}
											aria-label={`订单 ${contractPopoverOrderLabel} 合同编号`}
										/>
										<Button
											size='sm'
											variant='primary'
											className='text-nowrap flex-shrink-0 fs-10 px-2'
											onClick={() => handleSaveOrderContracts(editingContractOrderId)}
											disabled={savingOrderId === editingContractOrderId}
										>
											{savingOrderId === editingContractOrderId ? '…' : '保存'}
										</Button>
									</div>
									{contractSaveErrorByOrderId[editingContractOrderId] ? (
										<span className='text-danger' style={{ fontSize: '10px', lineHeight: 1.2 }}>
											{contractSaveErrorByOrderId[editingContractOrderId]}
										</span>
									) : null}
								</div>
							</div>
						</div>
					</div>,
					document.body,
				)}
			{typeof document !== 'undefined' &&
				step === 'preview' &&
				editingInvoiceOrderId &&
				statementInvoicePopoverPos &&
				createPortal(
					<div
						ref={statementInvoicePopoverPanelRef}
						style={{
							position: 'fixed',
							top: statementInvoicePopoverPos.top,
							left: statementInvoicePopoverPos.left,
							zIndex: 1080,
							pointerEvents: 'auto',
						}}
					>
						<div
							id='statement-pdf-invoice-popover'
							className='shadow-sm position-relative'
							style={{
								maxWidth: 'min(320px, calc(100vw - 16px))',
								margin: 0,
								borderRadius: '6px',
								border: `1px solid ${isDark ? '#475569' : '#dee2e6'}`,
								backgroundColor: isDark ? '#1e293b' : '#fff',
								...(isDark ? { backgroundColor: '#1e293b', borderColor: '#475569', color: '#e2e8f0' } : {}),
							}}
						>
							<div
								style={{
									position: 'absolute',
									top: -7,
									left: statementInvoicePopoverPos?.arrowLeft ?? 24,
									transform: 'translateX(-50%)',
									width: 14,
									height: 8,
									backgroundColor: isDark ? '#1e293b' : '#fff',
									clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
									pointerEvents: 'none',
									zIndex: 2,
								}}
							/>
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: statementInvoicePopoverPos?.arrowLeft ?? 24,
									transform: 'translateX(-50%)',
									width: 16,
									height: 1,
									backgroundColor: isDark ? '#1e293b' : '#fff',
									pointerEvents: 'none',
									zIndex: 2,
								}}
							/>
							<div className={isDark ? 'text-light' : ''} style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 5, paddingBottom: 10 }}>
								<div className='d-flex align-items-center gap-2' style={{ marginBottom: '8px', minHeight: '1.25rem' }}>
									<div
										className={`flex-grow-1 min-w-0 text-break lh-sm ${isDark ? '' : 'text-muted'}`}
										style={{
											fontSize: '11px',
											fontWeight: 500,
											letterSpacing: '0.02em',
											paddingRight: '6px',
											lineHeight: 1.35,
											...(isDark ? { color: '#94a3b8' } : {}),
										}}
									>
										订单 {invoicePopoverOrderLabel} · 发票号码
									</div>
								</div>
								<div className='d-flex flex-column gap-2'>
									<div className='d-flex gap-2 align-items-center'>
										<Form.Control
											ref={invoiceInputRef}
											size='sm'
											className={`fs-10 ${isDark ? 'bg-dark text-white border-secondary' : ''}`}
											value={invoiceDraftByOrderId[editingInvoiceOrderId] ?? ''}
											onChange={(e) =>
												setInvoiceDraftByOrderId((prev) => ({
													...prev,
													[editingInvoiceOrderId]: e.target.value,
												}))
											}
											placeholder='请输入发票号码'
											disabled={savingOrderId === editingInvoiceOrderId}
											aria-label={`订单 ${invoicePopoverOrderLabel} 发票号码`}
										/>
										<Button
											size='sm'
											variant='primary'
											className='text-nowrap flex-shrink-0 fs-10 px-2'
											onClick={() => handleSaveOrderInvoices(editingInvoiceOrderId)}
											disabled={savingOrderId === editingInvoiceOrderId}
										>
											{savingOrderId === editingInvoiceOrderId ? '…' : '保存'}
										</Button>
									</div>
									{invoiceSaveErrorByOrderId[editingInvoiceOrderId] ? (
										<span className='text-danger' style={{ fontSize: '10px', lineHeight: 1.2 }}>
											{invoiceSaveErrorByOrderId[editingInvoiceOrderId]}
										</span>
									) : null}
								</div>
							</div>
						</div>
					</div>,
					document.body,
				)}
			<Modal.Footer className='gap-2' style={previewModalFooterStyle}>
				{step === 'preview' ? (
					<Button variant='outline-secondary' onClick={() => setStep('range')}>
						返回修改区间
					</Button>
				) : null}
				{step === 'range' ? (
					<Button variant='primary' onClick={handleGenerate} disabled={selectedBuckets.size === 0 || statementPdfConfigLoading}>
						生成对账单预览
					</Button>
				) : (
					<Button variant='primary' onClick={handleDownloadPdf} disabled={pdfGenerating || !statementPdfPreviewReady}>
						{pdfGenerating ? '正在生成 PDF…' : '下载 PDF'}
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default StatementPdfPreviewModal;
